import React, { useState, useEffect, useRef } from "react";
import { Github, Linkedin, Mail, ChevronDown, Sparkles, Code2, Zap, Cpu, Code, Terminal, Users, Database, Layers , Brain, Cloud} from "lucide-react";
import { motion } from "framer-motion";
import ProjectsTimeline from "../components/WorkExperienceTimeline";
import TechStackSection from "../components/TechStackSection";
import ProjectsSection from "../components/ProjectsSection";
import LetsConnect from "../components/LetsConnect";

const matrixChars = "01AI";
const matrixArray = Array(100)
    .fill("")
    .map(() => matrixChars[Math.floor(Math.random() * matrixChars.length)]);
    const cards = [
        {
          icon: Brain,
          title: "AI/ML Engineering",
          description: "Specialized in deep learning, NLP, and computer vision systems. Building intelligent solutions that scale."
        },
        {
          icon: Code2,
          title: "Full Stack Development",
          description: "Creating robust web applications with modern tech stacks and best practices."
        },
        {
          icon: Database,
          title: "Data Engineering",
          description: "Designing efficient data pipelines and storage solutions for large-scale systems."
        },
        {
          icon: Cloud,
          title: "Cloud Architecture",
          description: "Implementing cloud-native solutions with focus on scalability and reliability."
        },
        {
          icon: Terminal,
          title: "DevOps Practices",
          description: "Streamlining development workflows with automated CI/CD pipelines."
        },
        {
          icon: Cpu,
          title: "System Design",
          description: "Architecting scalable systems that handle complex requirements efficiently."
        }
      ];
    
      // Split cards into two columns
      const leftColumn = cards.slice(0, Math.ceil(cards.length / 2));
      const rightColumn = cards.slice(Math.ceil(cards.length / 2));

const HeroSection = () => {
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const projectsRef = useRef<HTMLDivElement | null>(null); 

    const scrollToProjects = () => {
        projectsRef.current?.scrollIntoView({ behavior: "smooth" });
    };


    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            setMousePosition({ x: e.clientX, y: e.clientY });
        };

        window.addEventListener("mousemove", handleMouseMove);
        return () => window.removeEventListener("mousemove", handleMouseMove);
    }, []);

    return (
        <div className="relative min-h-screen bg-black overflow-hidden">
            {/* Matrix Background Animation */}
            <div className="fixed inset-0 opacity-20 pointer-events-none">
                {matrixArray.map((char, i) => (
                    <div
                        key={i}
                        className="absolute text-green-400 animate-matrix font-mono"
                        style={{
                            left: `${(i / matrixArray.length) * 100}%`,
                            animationDelay: `${Math.random() * 5}s`,
                            fontSize: `${Math.random() * 16 + 12}px`,
                        }}
                    >
                        {char}
                    </div>
                ))}
            </div>

            {/* Dynamic Cursor Light Effect */}
            <div
                className="fixed w-[600px] h-[600px] pointer-events-none transition-transform duration-100 ease-out"
                style={{
                    background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
                    transform: `translate(${mousePosition.x - 300}px, ${mousePosition.y - 300}px)`,
                }}
            >
                <div className="absolute inset-0 animate-pulse-ring rounded-full "></div>
                <div className="absolute inset-0 animate-pulse-ring rounded-full " style={{ animationDelay: "1s" }}></div>
                <div className="absolute inset-0 animate-pulse-ring rounded-full " style={{ animationDelay: "2s" }}></div>
            </div>

            {/* Floating Background Elements */}
            <div className="absolute inset-0">
                <div className="absolute w-full h-full">
                    {[...Array(50)].map((_, i) => (
                        <div
                            key={i}
                            className="absolute bg-white/[0.001] backdrop-blur-[0.5px] border border-white/[0.05] rounded-lg transform rotate-45"
                            style={{
                                width: Math.random() * 100 + 50 + "px",
                                height: Math.random() * 100 + 50 + "px",
                                top: Math.random() * 100 + "%",
                                left: Math.random() * 100 + "%",
                                animation: `float ${Math.random() * 10 + 5}s ease-in-out infinite`,
                                animationDelay: `-${Math.random() * 5}s`,
                            }}
                        ></div>
                    ))}
                </div>
            </div>

            {/* Hero Content */}
            <div className="relative min-h-screen flex items-center px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto w-full">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        {/* Left Content - Name & Title */}
                        <div className="order-2 lg:order-1">
                            <div className="relative backdrop-blur-sm bg-white/[0.05] p-10 rounded-2xl border border-white/10 shadow-lg">
                                <div className="absolute -left-6 -top-6">
                                    <Sparkles className="w-8 h-8 text-white animate-spin-slow" />
                                </div>
                                <h2 className="text-white/70 font-medium text-lg mb-3 pl-6">
                                    Hello World, I'm
                                </h2>

                                {/* Animated Name */}
                                <h1 className="text-5xl sm:text-7xl font-bold mb-6 tracking-tight">
                                    <span className="relative group">
                                        <span className="bg-white bg-clip-text text-transparent">
                                            Yesvanthraja D
                                        </span>
                                        <span className="absolute -bottom-2 left-0 w-full h-1 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></span>
                                    </span>
                                </h1>

                                {/* Subtitle */}
                                <p className="text-2xl sm:text-3xl font-medium text-gray-300 mb-8 leading-relaxed">
                                    AI Engineer | Full Stack Software Developer @ Hewlett Packard Enterprise
                                </p>

                                {/* CTA Buttons */}
                                <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <a
                  href="/contact"
                  className="group px-8 py-3 bg-transparent text-white font-medium rounded-full border border-white/20 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-white/25 relative overflow-hidden"
                >
                  <span className="relative z-10">Get in Touch</span>
                </a>
                {/* Smooth Scroll Button */}
                <button
                  onClick={scrollToProjects}
                  className="group px-8 py-3 bg-transparent text-white font-medium rounded-full border border-white/20 transition-all duration-300 transform hover:scale-105 hover:bg-white/10 hover:border-white/40"
                >
                  View My Work
                </button>
              </div>

                                {/* Social Links */}
                                <div className="flex space-x-6">
                                    <a href="https://github.com/yesvanthraja" target="_blank" className="text-gray-500 hover:text-green-400 transition-all duration-300 transform hover:scale-110">
                                        <Github className="w-6 h-6" />
                                    </a>
                                    <a href="https://www.linkedin.com/in/yesvanthraja-d-7841b324a/" target="_blank" className="text-gray-500 hover:text-green-400 transition-all duration-300 transform hover:scale-110">
                                        <Linkedin className="w-6 h-6" />
                                    </a>
                                    <a href="mailto:yeshuraja07@gmail.com" className="text-gray-500 hover:text-green-400 transition-all duration-300 transform hover:scale-110">
                                        <Mail className="w-6 h-6" />
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Image with Effects */}
                        {/* <div className="order-1 lg:order-2 relative">
              <div className="relative group">
                <div className="absolute -inset-4 bg-green-500/20 rounded-full blur-2xl opacity-20 group-hover:opacity-30 transition-opacity duration-500"></div>
                <div className="relative aspect-square rounded-3xl overflow-hidden border border-white/10 backdrop-blur-xl bg-white/5 shadow-lg">
                  <img
                    src="/assets/myImage.jpeg"
                    alt="Your Name"
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 grayscale contrast-125"
                  />
                </div>
              </div>
            </div> */}
                        {/* Card Container */}
                        <div className="order-1 lg:order-2 relative">
                            <div className="relative bg-white border border-gray-800 rounded-2xl shadow-lg p-2 max-w-lg text-center">
                                <div className="overflow-hidden rounded-lg border-8 border-white shadow-md">
                                    <img
                                        src="/assets/myImage.jpeg"
                                        alt="Your Name"
                                        className="w-full h-auto object-cover"
                                    />
                                </div>
                            </div>
                        </div>
                        {/* <div className="order-1 lg:order-2 relative h-screen flex items-center justify-center">
                            <motion.div
                                className="relative bg-white border border-gray-800 rounded-2xl shadow-lg p-2 max-w-lg text-center"
                                animate={{
                                    x: [0, 40, -30, 60, -50, 20, 0], // More complex random motion
                                    y: [0, 25, -15, 30, -20, 10, 0], // Adds variation to motion
                                    rotate: [0, 2, -2, 3, -3, 1, 0], // Slight tilt for natural feel
                                    scale: [1, 1.02, 1, 1.03, 1], // Subtle scaling for depth effect
                                }}
                                transition={{
                                    duration: 8, // Slower, more elegant motion
                                    repeat: Infinity, // Loop forever
                                    repeatType: "mirror", // Reverses path smoothly
                                    ease: "easeInOut", // Soft transition between movements
                                }}
                            >
                                <div className="overflow-hidden rounded-lg border-8 border-white shadow-md">
                                    <img
                                        src="/assets/myImage.jpeg"
                                        alt="Your Name"
                                        className="w-full h-auto object-cover"
                                    />
                                </div>
                            </motion.div>
                        </div> */}
                    </div>
                </div>
            </div>
            
            {/* <section className="relative py-32 px-6 lg:px-20 text-white border-t border-gray-800 flex flex-col lg:flex-row items-center lg:items-start">
            <motion.div
                className="lg:w-1/2 text-left mt-16"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1 }}
            >
                <h2 className="text-5xl font-bold bg-gradient-to-r from-white via-gray-400 to-white bg-clip-text text-transparent">
                    About Me
                </h2>
                <p className="mt-6 text-lg text-gray-400 leading-relaxed">
                    I am an **AI Engineer** and **Full Stack Developer** at **HPE**, passionate about **building intelligent systems**.
                    My expertise includes **Machine Learning, NLP, Computer Vision, and Scalable Web Applications**, driving AI innovation.
                </p>
            </motion.div>

            <div className="lg:w-1/2 mt-10 lg:mt-0 relative flex flex-wrap justify-center items-center overflow-hidden">
                {cards.map((card, index) => (
                    <motion.div
                        key={index}
                        className={`relative w-56 bg-black border border-gray-700 p-6 rounded-xl shadow-lg transition-all duration-500 hover:scale-105 hover:border-gray-500`}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.2 * index }}
                        style={{
                            animation: `move-card ${index % 2 === 0 ? "6s" : "7s"} linear infinite alternate`,
                        }}
                    >
                        <div className="flex flex-col items-center text-center">
                            <card.icon className="w-12 h-12 text-gray-300 mb-4 transition-all duration-300" />
                            <h3 className="text-lg font-semibold text-white">{card.title}</h3>
                            <p className="text-gray-300 text-sm mt-2">{card.description}</p>
                        </div>
                    </motion.div>
                ))}
            </div>
            
        </section> */}
        <ChevronDown className="w-6 h-6 text-white bottom-8 left-1/2 transform -translate-x-3/2 animate-bounce" />
         <section className="relative min-h-screen  text-white py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 " />

      <div className="max-w-7xl mx-auto relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-24"
        >
          <h2 className="text-4xl sm:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-gray-400 to-white bg-clip-text text-transparent">
            Expertise & Skills
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Building innovative solutions at the intersection of AI and scalable applications
          </p>
        </motion.div>

        {/* Two-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Left Column */}
          <div className="space-y-8">
            {leftColumn.map((card, index) => (
              <motion.div
                key={index}
                className="relative group rounded-xl"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                animate={{
                  y: [0, index % 2 === 0 ? -20 : 20],
                  transition: {
                    duration: 3,
                    repeat: Infinity,
                    repeatType: "reverse",
                    delay: index * 0.2
                  }
                }}
              >
                
                {/* Card Content */}
                <div className="relative bg-black/50 backdrop-blur-sm border border-white/10 p-6 rounded-xl hover:border-white/20 transition-all duration-300">
                  <div className="flex items-center gap-4 mb-4">
                    <card.icon className="w-8 h-8 text-green-400" />
                    <h3 className="text-xl font-semibold">{card.title}</h3>
                  </div>
                  <p className="text-gray-400">{card.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {rightColumn.map((card, index) => (
              <motion.div
                key={index}
                className="relative group rounded-xl"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                animate={{
                  y: [0, index % 2 === 0 ? 20 : -20],
                  transition: {
                    duration: 3,
                    repeat: Infinity,
                    repeatType: "reverse",
                    delay: index * 0.2
                  }
                }}
              >
                
                {/* Card Content */}
                <div className="relative bg-black/50 backdrop-blur-sm border border-white/10 p-6 rounded-xl hover:border-white/20 transition-all duration-300">
                  <div className="flex items-center gap-4 mb-4">
                    <card.icon className="w-8 h-8 text-green-400" />
                    <h3 className="text-xl font-semibold">{card.title}</h3>
                  </div>
                  <p className="text-gray-400">{card.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-24 text-center"
        >
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Currently at Hewlett Packard Enterprise, focusing on developing cutting-edge AI solutions 
            and enterprise-grade applications that push the boundaries of what's possible.
          </p>
        </motion.div>
      </div>
    </section>
   
    <ProjectsTimeline />
    <TechStackSection/>
    <div ref={projectsRef}>
        <ProjectsSection />
      </div>
    <LetsConnect/>
   
            {/* Scroll Indicator */}
            
        </div>
    );
};

export default HeroSection;




// import React, { useState, useEffect } from "react";
// import { Github, Linkedin, Mail, ChevronDown, Sparkles, Code2, Zap, Cpu, Code, Terminal, Users, Database, Layers } from "lucide-react";
// import { motion } from "framer-motion";

// const matrixChars = "01AI";
// const matrixArray = Array(100)
//     .fill("")
//     .map(() => matrixChars[Math.floor(Math.random() * matrixChars.length)]);


// const aboutCards = [
//     {
//         title: "AI & Machine Learning",
//         description: "Exploring deep learning, NLP, and AI-driven solutions.",
//         icon: Cpu,
//         person: "Alan Turing",
//         company: "AI Research",
//         img: "/assets/alan.jpg",
//     },
//     {
//         title: "Full Stack Engineering",
//         description: "Building modern, scalable applications using React and Node.js.",
//         icon: Code,
//         person: "Grace Hopper",
//         company: "Software Pioneer",
//         img: "/assets/grace.jpg",
//     },
//     {
//         title: "Cloud & DevOps",
//         description: "Deploying AI models using Kubernetes, AWS, and cloud technologies.",
//         icon: Database,
//         person: "Linus Torvalds",
//         company: "Linux Foundation",
//         img: "/assets/linus.jpg",
//     },
//     {
//         title: "AI Automation",
//         description: "Optimizing workflows through intelligent automation & decision-making.",
//         icon: Terminal,
//         person: "Ada Lovelace",
//         company: "Mathematics & Computing",
//         img: "/assets/ada.jpg",
//     },
//     {
//         title: "Product Engineering",
//         description: "Crafting next-gen AI-powered software architectures.",
//         icon: Layers,
//         person: "Elon Musk",
//         company: "Tesla & SpaceX",
//         img: "/assets/elon.jpg",
//     },
//     {
//         title: "Intelligent Systems",
//         description: "Developing AI systems that enhance human capabilities.",
//         icon: Sparkles,
//         person: "Sundar Pichai",
//         company: "Google AI",
//         img: "/assets/sundar.jpg",
//     },
// ];

// const HeroSection = () => {
//     const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

//     useEffect(() => {
//         const handleMouseMove = (e: MouseEvent) => {
//             setMousePosition({ x: e.clientX, y: e.clientY });
//         };

//         window.addEventListener("mousemove", handleMouseMove);
//         return () => window.removeEventListener("mousemove", handleMouseMove);
//     }, []);

//     return (
//         <div className="relative min-h-screen bg-black overflow-hidden">
//             {/* Matrix Background Animation */}
//             <div className="fixed inset-0 opacity-20 pointer-events-none">
//                 {matrixArray.map((char, i) => (
//                     <div
//                         key={i}
//                         className="absolute text-green-400 animate-matrix font-mono"
//                         style={{
//                             left: `${(i / matrixArray.length) * 100}%`,
//                             animationDelay: `${Math.random() * 5}s`,
//                             fontSize: `${Math.random() * 16 + 12}px`,
//                         }}
//                     >
//                         {char}
//                     </div>
//                 ))}
//             </div>

//             {/* Dynamic Cursor Light Effect */}
//             <div
//                 className="fixed w-[600px] h-[600px] pointer-events-none transition-transform duration-100 ease-out"
//                 style={{
//                     background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
//                     transform: `translate(${mousePosition.x - 300}px, ${mousePosition.y - 300}px)`,
//                 }}
//             >
//                 <div className="absolute inset-0 animate-pulse-ring rounded-full "></div>
//                 <div className="absolute inset-0 animate-pulse-ring rounded-full " style={{ animationDelay: "1s" }}></div>
//                 <div className="absolute inset-0 animate-pulse-ring rounded-full " style={{ animationDelay: "2s" }}></div>
//             </div>

//             {/* Floating Background Elements */}
//             <div className="absolute inset-0">
//                 <div className="absolute w-full h-full">
//                     {[...Array(50)].map((_, i) => (
//                         <div
//                             key={i}
//                             className="absolute bg-white/[0.001] backdrop-blur-[0.5px] border border-white/[0.05] rounded-lg transform rotate-45"
//                             style={{
//                                 width: Math.random() * 100 + 50 + "px",
//                                 height: Math.random() * 100 + 50 + "px",
//                                 top: Math.random() * 100 + "%",
//                                 left: Math.random() * 100 + "%",
//                                 animation: `float ${Math.random() * 10 + 5}s ease-in-out infinite`,
//                                 animationDelay: `-${Math.random() * 5}s`,
//                             }}
//                         ></div>
//                     ))}
//                 </div>
//             </div>

//             {/* Hero Content */}
//             <div className="relative min-h-screen flex items-center px-4 sm:px-6 lg:px-8">
//                 <div className="max-w-7xl mx-auto w-full">
//                     <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
//                         {/* Left Content - Name & Title */}
//                         <div className="order-2 lg:order-1">
//                             <div className="relative backdrop-blur-sm bg-white/[0.05] p-10 rounded-2xl border border-white/10 shadow-lg">
//                                 <div className="absolute -left-6 -top-6">
//                                     <Sparkles className="w-8 h-8 text-white animate-spin-slow" />
//                                 </div>
//                                 <h2 className="text-white/70 font-medium text-lg mb-3 pl-6">
//                                     Hello World, I'm
//                                 </h2>

//                                 {/* Animated Name */}
//                                 <h1 className="text-5xl sm:text-7xl font-bold mb-6 tracking-tight">
//                                     <span className="relative group">
//                                         <span className="bg-white bg-clip-text text-transparent">
//                                             Yesvanthraja D
//                                         </span>
//                                         <span className="absolute -bottom-2 left-0 w-full h-1 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></span>
//                                     </span>
//                                 </h1>

//                                 {/* Subtitle */}
//                                 <p className="text-2xl sm:text-3xl font-medium text-gray-300 mb-8 leading-relaxed">
//                                     AI Engineer | Full Stack Software Developer @ Hewlett Packard Enterprise
//                                 </p>

//                                 {/* CTA Buttons */}
//                                 <div className="flex flex-col sm:flex-row gap-4 mb-12">
//                                     <a
//                                         href="#contact"
//                                         className="group px-8 py-3 bg-transparent text-white font-medium rounded-full  border border-white/20 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-white/25 relative overflow-hidden"
//                                     >
//                                         <span className="relative z-10">Get in Touch</span>
//                                     </a>
//                                     <a
//                                         href="#portfolio"
//                                         className="group px-8 py-3 bg-transparent text-white font-medium rounded-full border border-white/20 transition-all duration-300 transform hover:scale-105 hover:bg-white/10 hover:border-white/40"
//                                     >
//                                         View My Work
//                                     </a>
//                                 </div>

//                                 {/* Social Links */}
//                                 <div className="flex space-x-6">
//                                     <a href="#" className="text-gray-500 hover:text-green-400 transition-all duration-300 transform hover:scale-110">
//                                         <Github className="w-6 h-6" />
//                                     </a>
//                                     <a href="#" className="text-gray-500 hover:text-green-400 transition-all duration-300 transform hover:scale-110">
//                                         <Linkedin className="w-6 h-6" />
//                                     </a>
//                                     <a href="#" className="text-gray-500 hover:text-green-400 transition-all duration-300 transform hover:scale-110">
//                                         <Mail className="w-6 h-6" />
//                                     </a>
//                                 </div>
//                             </div>
//                         </div>

//                         {/* Right Side - Image with Effects */}
//                         {/* <div className="order-1 lg:order-2 relative">
//               <div className="relative group">
//                 <div className="absolute -inset-4 bg-green-500/20 rounded-full blur-2xl opacity-20 group-hover:opacity-30 transition-opacity duration-500"></div>
//                 <div className="relative aspect-square rounded-3xl overflow-hidden border border-white/10 backdrop-blur-xl bg-white/5 shadow-lg">
//                   <img
//                     src="/assets/myImage.jpeg"
//                     alt="Your Name"
//                     className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 grayscale contrast-125"
//                   />
//                 </div>
//               </div>
//             </div> */}
//                         {/* Card Container */}
//                         {/* <div className="order-1 lg:order-2 relative">
//                             <div className="relative bg-white border border-gray-800 rounded-2xl shadow-lg p-2 max-w-lg text-center">
//                                 <div className="overflow-hidden rounded-lg border-8 border-white shadow-md">
//                                     <img
//                                         src="/assets/myImage.jpeg"
//                                         alt="Your Name"
//                                         className="w-full h-auto object-cover"
//                                     />
//                                 </div>
//                             </div>
//                         </div> */}
//                         <div className="order-1 lg:order-2 relative h-screen flex items-center justify-center">
//                             {/* Floating Image Card */}
//                             <motion.div
//                                 className="relative bg-white border border-gray-800 rounded-2xl shadow-lg p-2 max-w-lg text-center"
//                                 animate={{
//                                     x: [0, 40, -30, 60, -50, 20, 0], // More complex random motion
//                                     y: [0, 25, -15, 30, -20, 10, 0], // Adds variation to motion
//                                     rotate: [0, 2, -2, 3, -3, 1, 0], // Slight tilt for natural feel
//                                     scale: [1, 1.02, 1, 1.03, 1], // Subtle scaling for depth effect
//                                 }}
//                                 transition={{
//                                     duration: 8, // Slower, more elegant motion
//                                     repeat: Infinity, // Loop forever
//                                     repeatType: "mirror", // Reverses path smoothly
//                                     ease: "easeInOut", // Soft transition between movements
//                                 }}
//                             >
//                                 {/* Image Container */}
//                                 <div className="overflow-hidden rounded-lg border-8 border-white shadow-md">
//                                     <img
//                                         src="/assets/myImage.jpeg"
//                                         alt="Your Name"
//                                         className="w-full h-auto object-cover"
//                                     />
//                                 </div>
//                             </motion.div>
//                         </div>
//                     </div>
//                 </div>
//             </div>

//             {/* <section className="relative py-32 px-6 lg:px-20 text-white border-t border-gray-800 flex flex-col lg:flex-row items-center lg:items-start">
//             <motion.div
//                 className="lg:w-1/2 text-left mt-16"
//                 initial={{ opacity: 0, x: -50 }}
//                 animate={{ opacity: 1, x: 0 }}
//                 transition={{ duration: 1 }}
//             >
//                 <h2 className="text-5xl font-bold bg-gradient-to-r from-white via-gray-400 to-white bg-clip-text text-transparent">
//                     About Me
//                 </h2>
//                 <p className="mt-6 text-lg text-gray-400 leading-relaxed">
//                     I am an **AI Engineer** and **Full Stack Developer** at **HPE**, passionate about **building intelligent systems**.
//                     My expertise includes **Machine Learning, NLP, Computer Vision, and Scalable Web Applications**, driving AI innovation.
//                 </p>
//             </motion.div>

//             <div className="lg:w-1/2 mt-10 lg:mt-0 relative flex flex-wrap justify-center items-center overflow-hidden">
//                 {cards.map((card, index) => (
//                     <motion.div
//                         key={index}
//                         className={`relative w-56 bg-black border border-gray-700 p-6 rounded-xl shadow-lg transition-all duration-500 hover:scale-105 hover:border-gray-500`}
//                         initial={{ opacity: 0, y: 30 }}
//                         animate={{ opacity: 1, y: 0 }}
//                         transition={{ duration: 1, delay: 0.2 * index }}
//                         style={{
//                             animation: `move-card ${index % 2 === 0 ? "6s" : "7s"} linear infinite alternate`,
//                         }}
//                     >
//                         <div className="flex flex-col items-center text-center">
//                             <card.icon className="w-12 h-12 text-gray-300 mb-4 transition-all duration-300" />
//                             <h3 className="text-lg font-semibold text-white">{card.title}</h3>
//                             <p className="text-gray-300 text-sm mt-2">{card.description}</p>
//                         </div>
//                     </motion.div>
//                 ))}
//             </div>
            
//         </section> */}
//             <section className="relative py-32 px-6 lg:px-20 text-white border-t border-gray-800">
//                 {/* Header */}
//                 <div className="max-w-4xl mx-auto text-center">
//                     <h2 className="text-5xl font-bold bg-gradient-to-r from-white via-gray-400 to-white bg-clip-text text-transparent">
//                         About Me
//                     </h2>
//                     <p className="mt-4 text-lg text-gray-400">
//                         AI Engineer | Full Stack Developer @ Hewlett Packard Enterprise
//                         Passionate about **Machine Learning, NLP, Cloud Infrastructure, and Scalable Web Apps**.
//                     </p>
//                 </div>

//                 {/* Card Scrolling Section */}
//                 <div className="relative mt-16 overflow-hidden">
//                     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
//                         {/* First Row - Moves Up */}
//                         <motion.div
//                             className="flex flex-col gap-6"
//                             animate={{ y: ["0%", "-10%", "0%"], opacity: [0.9, 1, 0.9] }}
//                             transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
//                         >
//                             {aboutCards.slice(0, 2).map((card, index) => (
//                                 <motion.div
//                                     key={index}
//                                     className="relative bg-black border border-gray-800 p-6 rounded-2xl shadow-lg transition-all duration-500 hover:scale-105 overflow-hidden"
//                                     whileHover={{
//                                         background: "linear-gradient(135deg, #4F46E5, #9333EA)",
//                                         borderColor: "#9333EA",
//                                         color: "#FFF",
//                                     }}
//                                 >
//                                     {/* Gradient Overlay */}
//                                     <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80"></div>

//                                     <card.icon className="w-10 h-10 text-gray-300 mb-4" />
//                                     <h3 className="text-lg font-semibold text-white">{card.title}</h3>
//                                     <p className="text-gray-300 text-sm mt-2">{card.description}</p>

//                                     {/* User Info */}
//                                 </motion.div>
//                             ))}
//                         </motion.div>

//                         {/* Second Row - Moves Down */}
//                         <motion.div
//                             className="flex flex-col gap-6"
//                             animate={{ y: ["-10%", "0%", "-10%"], opacity: [0.9, 1, 0.9] }}
//                             transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
//                         >
//                             {aboutCards.slice(2, 3).map((card, index) => (
//                                 <motion.div
//                                     key={index}
//                                     className="relative bg-black border border-gray-800 p-6 rounded-2xl shadow-lg transition-all duration-500 hover:scale-105 overflow-hidden"
//                                     whileHover={{
//                                         background: "linear-gradient(135deg, #4F46E5, #9333EA)",
//                                         borderColor: "#9333EA",
//                                         color: "#FFF",
//                                     }}
//                                 >
//                                     {/* Gradient Overlay */}
//                                     <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80"></div>

//                                     <card.icon className="w-10 h-10 text-gray-300 mb-4" />
//                                     <h3 className="text-lg font-semibold text-white">{card.title}</h3>
//                                     <p className="text-gray-300 text-sm mt-2">{card.description}</p>

//                                 </motion.div>
//                             ))}
//                         </motion.div>
//                         <motion.div
//                             className="flex flex-col gap-6"
//                             animate={{ y: ["-10%", "0%", "-10%"], opacity: [0.9, 1, 0.8] }}
//                             transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
//                         >
//                             {aboutCards.slice(4, 6).map((card, index) => (
//                                 <motion.div
//                                     key={index}
//                                     className="relative bg-black border border-gray-800 p-6 rounded-2xl shadow-lg transition-all duration-500 hover:scale-105 overflow-hidden"
//                                     whileHover={{
//                                         borderColor: "#9333EA",
//                                         color: "#FFF",
//                                     }}
//                                 >
//                                     {/* 🟠 **Perfect Gradient Overlay** */}
//                                     <div className="absolute inset-0 bg-gradient-to-t from-[#8B5CF6] via-[#CD5C5C] to-transparent opacity-0 hover:opacity-100 transition-opacity duration-700"></div>

//                                     <card.icon className="w-10 h-10 text-gray-300 mb-4" />
//                                     <h3 className="text-lg font-semibold text-white">{card.title}</h3>
//                                     <p className="text-gray-300 text-sm mt-2">{card.description}</p>

//                                     {/* User Info */}
//                                     <div className="flex items-center mt-4">
//                                         <img
//                                             src={card.img}
//                                             alt={card.person}
//                                             className="w-8 h-8 rounded-full border-2 border-white mr-3"
//                                         />
//                                         <div>
//                                             <p className="text-sm text-white font-bold">{card.person}</p>
//                                             <p className="text-xs text-gray-400">{card.company}</p>
//                                         </div>
//                                     </div>
//                                 </motion.div>
//                             ))}
//                         </motion.div>
//                     </div>
//                 </div>
//             </section>
//             {/* Scroll Indicator */}
//             <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
//                 <ChevronDown className="w-6 h-6 text-white" />
//             </div>
//         </div>
//     );
// };

// export default HeroSection;
